"use client"

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Menu, X, Phone } from 'lucide-react'

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleShowModal = () => {
    // Dispatch custom event to trigger modal in the main page
    window.dispatchEvent(new CustomEvent('showQuoteModal'))
    setMobileMenuOpen(false)
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="h-[70px] grid grid-cols-[auto_1fr_auto] items-center gap-4">
              {/* Left Section - Logo */}
              <div className="flex justify-start">
                <Link href="/" className="flex items-center">
                  <Image
                    src="/images/Southwest-Homes-Logo.png"
                    alt="Southwest Homes"
                    width={220}
                    height={53}
                    className="h-[45px] md:h-[50px] w-auto"
                    loading="lazy"
                  />
                </Link>
              </div>

              {/* Center Section - Quote + Floor Plans */}
              <div className="flex flex-col items-center justify-center text-center px-4">
                <p className="text-gray-700 italic text-xs md:text-sm">
                  "Call me anytime with questions, I'm here to help."
                </p>
                <p className="text-gray-600 text-[10px] md:text-xs mt-0.5">
                  - R.J. Gade, Southwest Homes Sales Representative
                </p>
                <Link
                  href="/floor-plans"
                  className="mt-1 text-[#c88546] hover:text-[#b87439] text-sm font-medium transition-colors"
                >
                  Browse Floor Plans →
                </Link>
              </div>

              {/* Right Section - Phone & Button */}
              <div className="flex items-center justify-end gap-4">
                <a
                  href="tel:9283610298"
                  className="text-gray-900 text-base md:text-lg font-semibold hover:text-[#c88546] transition-colors"
                >
                  (928) 361-0298
                </a>
                <button
                  onClick={handleShowModal}
                  className="bg-[#c88546] hover:bg-[#b87439] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ease-in-out shadow-lg hover:shadow-xl"
                >
                  Get Free Buyer's Guide
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <div className="h-[60px] flex items-center justify-between">
              {/* Logo */}
              <Link href="/" className="flex items-center">
                <Image
                  src="/images/Southwest-Homes-Logo.png"
                  alt="Southwest Homes"
                  width={145}
                  height={35}
                  className="h-[35px] w-auto"
                  loading="lazy"
                />
              </Link>

              {/* Phone & Menu Button */}
              <div className="flex items-center gap-3">
                <a 
                  href="tel:9283610298" 
                  className="flex items-center text-[#c88546]"
                  aria-label="Call (928) 361-0298"
                >
                  <Phone className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-700 hover:text-[#c88546] transition-colors"
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-[60px] left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {/* Quote Section */}
              <div className="pb-3 mb-3 border-b border-gray-200">
                <p className="text-gray-600 italic text-sm text-center">
                  "Call me anytime with questions"
                </p>
                <p className="text-gray-600 text-xs text-center mt-1">
                  - R.J. Gade
                </p>
              </div>

              {/* Navigation Links */}
              <Link 
                href="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 px-3 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Home
              </Link>
              
              <Link
                href="/floor-plans"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 px-3 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Floor Plans
              </Link>

              <Link
                href="/financing"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 px-3 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Financing
              </Link>

              <Link
                href="/manufactured-homes"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 px-3 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Service Areas
              </Link>

              <Link
                href="/about"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 px-3 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                About Us
              </Link>

              {/* CTA Buttons */}
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-2">
                <button
                  onClick={handleShowModal}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                >
                  Get Free Buyer's Guide
                </button>
                
                <Link
                  href="/get-started"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full bg-[#c88546] hover:bg-[#b87439] text-white px-4 py-2.5 rounded-lg text-sm font-semibold text-center transition-colors"
                >
                  Request Info
                </Link>

                <a 
                  href="tel:9283610298" 
                  className="block w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold text-center transition-colors"
                >
                  Call (928) 361-0298
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40" 
          onClick={() => setMobileMenuOpen(false)}
          style={{ top: '60px' }}
        />
      )}
    </>
  )
}